import { useEffect, useState } from "react";
import { CONFIG } from "../config";
import { fmt } from "../lib/format";

/**
 * Le chiffre d'usage du produit.
 *
 * Un compteur est le levier de persuasion le plus banal du web, et le plus
 * souvent faux — c'est précisément pourquoi celui-ci est gouverné par une règle
 * plutôt que par une envie. Il affiche des nombres que le serveur compte
 * réellement, et **il se tait tant qu'ils ne veulent rien dire**.
 *
 * Le seuil vient du serveur, pas d'ici : c'est le même que celui de la
 * comparaison entre confrères, qui refuse de publier une distribution de douze
 * personnes en la présentant comme la mesure d'un métier. Un compteur d'usage
 * répond à la même question — « combien sont passés avant moi ? » — et mérite la
 * même réserve. En dessous, le serveur rend `null` et ce composant ne rend rien :
 * pas un zéro, pas un « bientôt », qui seraient des signaux en soi.
 *
 * **« N métiers couverts » a été retiré le 12/08**, le lendemain de son ajout.
 * Le chiffre était juste — les entrées du cache, réchauffage compris — et la
 * phrase se lisait trois lignes sous un champ qui promet « votre métier —
 * n'importe lequel ». Elle décrivait un catalogue fermé sur un produit dont
 * l'argument est de ne pas en avoir, et elle s'affichait à 2 : le visiteur
 * apprenait que le produit ne connaît presque rien, au moment de le convaincre.
 *
 * Aucun seuil ne rattrapait cela ; même à deux cents, « métiers couverts »
 * promet une liste. La taille de la base reste mesurée, sur `/sante`, où elle
 * sert à décider — et non à persuader.
 */

interface Usage {
  diagnostics: number | null;
}

export function Usage() {
  const [usage, setUsage] = useState<Usage>({ diagnostics: null });

  useEffect(() => {
    let vivant = true;
    fetch(CONFIG.activiteEndpoint)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((corps: { usage?: Usage }) => {
        if (vivant && corps.usage) setUsage(corps.usage);
      })
      .catch(() => {
        // Une source injoignable est traitée comme une source vide : pas de
        // chiffre plutôt qu'un chiffre inventé.
      });
    return () => {
      vivant = false;
    };
  }, []);

  if (usage.diagnostics === null) return null;

  return (
    <p className="usage" aria-live="polite">
      {fmt(usage.diagnostics)} diagnostics réalisés
    </p>
  );
}
